import { defineStore } from 'pinia'
import { useAuthStore } from '@/stores/auth'
import axios from '@/axios'

const STORAGE_KEY = 'notifications'
const POLL_MS = 30000
const DATABASE_SOURCE = 'database'
const LOCAL_SOURCE = 'local'

function adminTarget(adminTab, section) {
  return {
    path: '/site_admin',
    query: {
      admin_tab: adminTab,
      section
    }
  }
}

function approvalAdminTab(resourceType, roleCodes) {
  const roles = new Set((roleCodes || []).map(Number))

  switch (resourceType) {
    case 'user_detail':
    case 'branch':
      return roles.has(2) ? 'branch_admin' : null

    case 'heirloom':
      if (roles.has(3)) return 'archivist'
      if (roles.has(4)) return 'executor'
      return null

    case 'property':
      if (roles.has(3)) return 'archivist'
      if (roles.has(2)) return 'branch_admin'
      return null

    case 'recipe':
    case 'recipe_variation':
      if (roles.has(6)) return 'chef'
      if (roles.has(2)) return 'branch_admin'
      return null

    case 'reunion':
      return roles.has(1) ? 'site_admin' : null

    default:
      return null
  }
}

export function notificationTarget(item, roleCodes = []) {
  const bucketCode = String(item?.bucket_code || '')

  if (bucketCode === '1') {
    return adminTarget('branch_admin', 'pending_members')
  }

  if (bucketCode === 'support_email') {
    return adminTarget('site_admin', 'email')
  }

  if (bucketCode === '9') {
    const adminTab = approvalAdminTab(item?.resource_type, roleCodes)

    if (adminTab) {
      return adminTarget(adminTab, 'change_approvals')
    }
  }

  return null
}

export const useNotificationStore = defineStore('notifications', {
  state: () => ({
    items: JSON.parse(localStorage.getItem(STORAGE_KEY)) || [],
    loading: false,
    loaded: false,
    pollHandle: null,
    localNotificationVersion: 0
  }),

  getters: {
    count(state) {
      return state.items.length
    },

    sortedItems(state) {
      return [...state.items].sort((a, b) => {
        const aSort = Number(a.sort_order) || 9999
        const bSort = Number(b.sort_order) || 9999

        return aSort - bSort
      })
    },

    siteAdminNotificationCount(state) {
      return state.items
        .filter((item) => {
          return (
            item.source === DATABASE_SOURCE &&
            item.type === 'bucket' &&
            item.bucket_code === 'site_admin'
          )
        })
        .reduce((total, item) => {
          return total + Number(item.count || 0)
        }, 0)
    },

    supportEmailNotificationCount(state) {
      return state.items
        .filter((item) => {
          return (
            item.source === DATABASE_SOURCE &&
            item.type === 'bucket' &&
            item.bucket_code === 'support_email'
          )
        })
        .reduce((total, item) => {
          return total + Number(item.count || 0)
        }, 0)
    },

    branchAdminNotificationCount(state) {
      return state.items
        .filter((item) => {
          return (
            item.source === DATABASE_SOURCE &&
            item.type === 'bucket' &&
            item.bucket_code === 'branch_admin'
          )
        })
        .reduce((total, item) => {
          return total + Number(item.count || 0)
        }, 0)
    },

    adminNotificationCount(state) {
      return state.items
        .filter((item) => {
          return (
            item.source === DATABASE_SOURCE &&
            item.type === 'bucket' &&
            [
              'site_admin',
              'branch_admin'
            ].includes(item.bucket_code)
          )
        })
        .reduce((total, item) => {
          return total + Number(item.count || 0)
        }, 0)
    }
  },

  actions: {
    persist() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(this.items)
      )
    },

    setItems(items) {
      this.items = Array.isArray(items)
        ? items
        : []

      this.persist()
      this.loaded = true
    },

    setDatabaseItems(items) {
      const databaseItems = Array.isArray(items)
        ? items
        : []

      const localItems = this.items.filter((item) => {
        return item.source !== DATABASE_SOURCE
      })

      this.items = [
        ...databaseItems,
        ...localItems
      ]

      this.persist()
      this.loaded = true
    },

    addItem(item) {
      if (!item?.id) return

      const normalizedItem = {
        ...item,
        source: item.source || LOCAL_SOURCE
      }

      const index = this.items.findIndex((entry) => {
        return entry.id === normalizedItem.id
      })

      if (index === -1) {
        this.items.push(normalizedItem)

        if (normalizedItem.source === LOCAL_SOURCE) {
          this.localNotificationVersion += 1
        }
      } else {
        this.items[index] = normalizedItem
      }

      this.persist()
    },

    remove(id) {
      this.items = this.items.filter((item) => {
        return item.id !== id
      })

      this.persist()
    },

    clear() {
      this.items = []
      this.persist()
    },

    success(message) {
      this.addItem({
        id:
          `success-${Date.now()}-` +
          Math.random()
            .toString(36)
            .slice(2, 9),
        source: LOCAL_SOURCE,
        type: 'success',
        message
      })
    },

    error(message) {
      this.addItem({
        id:
          `error-${Date.now()}-` +
          Math.random()
            .toString(36)
            .slice(2, 9),
        source: LOCAL_SOURCE,
        type: 'error',
        message
      })
    },

    info(message) {
      this.addItem({
        id:
          `info-${Date.now()}-` +
          Math.random()
            .toString(36)
            .slice(2, 9),
        source: LOCAL_SOURCE,
        type: 'info',
        message
      })
    },

    normalizeApiItems(data) {
      if (!Array.isArray(data)) return []

      return data
        .map((item) => {
          if (item.type === 'bucket') {
            const count =
              Number(
                item.count ??
                item.unread_count
              ) || 0

            const label = String(
              item.notification_bucket || ''
            ).toLowerCase()
            const preview = item.preview_json || {}
            const normalized = {
              ...preview,
              id:
                item.id ||
                `bucket-${item.bucket_code}`,
              source: DATABASE_SOURCE,
              type: 'bucket',
              bucket_code: String(item.bucket_code),
              count,
              message:
                item.message ||
                `${count} ${label}`,
              sort_order:
                Number(item.sort_order) ||
                9999
            }

            return {
              ...normalized,
              target: notificationTarget(
                normalized,
                useAuthStore().adminRoleCodes
              )
            }
          }

          return {
            id:
              item.id ||
              [
                'database',
                item.type || 'info',
                item.sort_order || 9999,
                item.message || ''
              ].join('-'),
            source: DATABASE_SOURCE,
            type: item.type || 'info',
            message: item.message || '',
            sort_order:
              Number(item.sort_order) ||
              9999
          }
        })
        .filter((item) => item.message)
    },

    async fetchNotifications(
      { silent = false } = {}
    ) {
      const userDetailId = useAuthStore().user?.user_detail_id

      if (!userDetailId) return

      if (!silent) {
        this.loading = true
      }

      try {
        const { data } = await axios.get(
          `/api/family_members/${userDetailId}/notifications`,
          {
            withCredentials: true
          }
        )

        if (useAuthStore().user?.user_detail_id !== userDetailId) return

        this.setDatabaseItems(
          this.normalizeApiItems(data)
        )
      } catch (err) {
        console.error(
          'Failed to fetch notifications',
          err
        )
      } finally {
        if (!silent) {
          this.loading = false
        }
      }
    },

    async init() {
      await this.fetchNotifications()
      this.startPolling()
    },

    resetStore() {
      this.stopPolling()
      this.items = []
      this.loading = false
      this.loaded = false
      this.localNotificationVersion = 0
      this.persist()
    },

    startPolling() {
      if (this.pollHandle) return

      this.pollHandle = window.setInterval(() => {
        this.fetchNotifications({
          silent: true
        })
      }, POLL_MS)
    },

    stopPolling() {
      if (!this.pollHandle) return

      window.clearInterval(
        this.pollHandle
      )

      this.pollHandle = null
    }
  }
})
